import truncate from 'lodash/truncate'

export const isPresent = (value: any) => {
  const parsedValue = typeof value === 'number' ? `${value}` : value

  return parsedValue && Object.keys(parsedValue).length > 0
}

export const toTitleCase = (str: string) => {
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

export const truncatedOptionLabel = (option: string, length: number = 16) => {
  return truncate(option, { length })
}

export const extractSignupForm = (response: SignupFormQuestionResponse[]) => {
  // https://stackoverflow.com/questions/45339065/typescript-empty-object-for-a-typed-variable
  const options = <SignupFormQuestionOptions>{}
  const questionsData = []

  for (let i = 0; i < response.length; i++) {
    const questionItem = response[i]

    const isValidSignupFormQuestion =
      !questionItem.deleted && questionItem.type !== 'SignupForm::TosQuestion'

    if (isValidSignupFormQuestion) {
      const { questionOptions, ...question } = questionItem
      questionsData.push(question)

      if (Array.isArray(questionOptions)) {
        questionOptions.forEach((option: SignupFormQuestionOptions) => {
          if (!option.deleted) {
            options[option.id] = {
              ...option,
              questionId: questionItem.id,
            }
          }
        })
      }
    }
  }

  return {
    questions: questionsData,
    options,
  }
}

// this will be used in the mui tab https://mui.com/material-ui/react-tabs/#basic-tabs
export const a11yProps = (key: string, index: number) => {
  return {
    id: `${key}-${index}`,
    'aria-controls': `${key}-tabpanel-${index}`,
  }
}

export const isEmptyObject = (value: any) => {
  return value && Object.keys(value).length === 0
}

export const isConditionalQuestion = (question: any, options: any) => {
  if (
    !question.hasTrigger ||
    typeof question.logicOperator !== 'boolean' ||
    !question.triggerQuestionId ||
    !question.triggerOptionId
  ) {
    return false
  }

  // check if the question's trigger question id and the id is same and the trigger option is present in the options object
  return (
    question.triggerQuestionId &&
    parseInt(question.id) &&
    checkObjectProperty(options, `${question.triggerOptionId}`)
  )
}

/**
 * Segregate questions into parent & conditional questions
 *
 * @param {Object} questions A hash of questions indexed by id
 * @param {Object} options A hash of options indexed by id.
 * @returns {Object}
 * {
 *    parentQuestions: <Array> sorted parent questions,
 *    conditionalQuestions: <Object> A hash of sorted conditional questions indexed by parent id
 * }
 * @public
 */
export const segregateQuestions = (questions: any, options: any) => {
  const parentQuestions: any = []
  const conditionalQuestions: any = {}

  for (let i = 0; i < questions.length; i++) {
    const question = questions[i]
    if (isConditionalQuestion(question, options)) {
      if (
        Object.hasOwnProperty.call(
          conditionalQuestions,
          question.triggerQuestionId,
        )
      ) {
        conditionalQuestions[question.triggerQuestionId].push(question)
        conditionalQuestions[question.triggerQuestionId].sort(
          (a: any, b: any) => a.sequence - b.sequence,
        )
      } else {
        conditionalQuestions[question.triggerQuestionId] = [question]
      }
    } else {
      parentQuestions.push(question)
    }
  }

  return {
    parentQuestions,
    conditionalQuestions,
  }
}

/**
 * @param {Object} question
 * @param {Object} answer
 * @param {Array} children
 * @returns {Array} A sorted array of triggered questions
 * @public
 */
export const triggeredQuestions = (
  question: any,
  answer: any,
  children: any,
) => {
  const questions: any = []

  children.forEach((child: any) => {
    const { logicOperator, triggerOptionId } = child
    if (typeof answer.id === 'number' || typeof answer.id === 'string') {
      // It's a radio/dropdown answer
      if (logicOperator) {
        // eslint-disable-next-line eqeqeq
        if (answer.id == triggerOptionId) {
          questions.push(child)
        }
      } else {
        // eslint-disable-next-line eqeqeq
        if (answer.id != triggerOptionId) {
          questions.push(child)
        }
      }
    } else if (Array.isArray(answer.id)) {
      // It's a checkbox answer
      if (logicOperator) {
        // eslint-disable-next-line eqeqeq
        if (answer.id.some((a: any) => a == triggerOptionId)) {
          questions.push(child)
        }
      } else {
        // eslint-disable-next-line eqeqeq
        if (
          answer.id.length > 0 &&
          answer.id.every((a: any) => a != triggerOptionId)
        ) {
          questions.push(child)
        }
      }
    }
  })

  return questions.sort((a: any, b: any) => a.sequence - b.sequence)
}

export const extractQuestionOptions = (
  options: SignupFormQuestionOptions,
  questionId: string,
) => {
  const questionOptions = []
  for (const key in options) {
    const option = options[key]
    if (option.questionId === questionId) {
      questionOptions.push(option)
    }
  }
  return questionOptions.sort((a, b) => a.sequence - b.sequence)
}

export const mappedSuburb = (suburbMappedAnswer: string, region: string) => {
  const [name = '', state = '', postalCode = ''] =
    suburbMappedAnswer?.split(',') || ''

  let usaSuburb: string[]
  switch (region) {
    case 'australia':
    case 'new_zealand':
      // if there is no state then just return the name
      if (!state) {
        return {
          name: suburbMappedAnswer,
          state: '',
          zipcode: 0,
          postalCode: '',
        }
      }
      return {
        name,
        state: state?.trim(),
        zipcode: 0,
        postalCode: '',
      }
    case 'canada':
      if (!state) {
        return {
          name: suburbMappedAnswer,
          state: '',
          zipcode: 0,
          postalCode: '',
        }
      }
      return {
        name,
        state: state?.trim(),
        postalCode: postalCode?.trim(),
        zipcode: 0,
      }
    case 'united_states':
      if (!state) {
        return {
          name: suburbMappedAnswer,
          state: '',
          zipcode: 0,
          postalCode: '',
        }
      }
      usaSuburb = state?.trim()?.split(' ')
      return {
        name,
        state: usaSuburb[0],
        postalCode: '',
        zipcode: parseInt(usaSuburb[1]),
      }
  }
}
/**
 * This method is used in the view participant page in suburb
 * @param suburbMappedAnswer
 * @param region
 * @returns
 */
export const getSuburbDefaultValue = (
  suburbMappedAnswer: string,
  region: string,
) => {
  if (suburbMappedAnswer && suburbMappedAnswer.length > 0) {
    return mappedSuburb(suburbMappedAnswer, region)
  }
} // can we move this to the specific features/container's helper
//, since this relates to suburb and question specific which might not be common for other scenarios

export const formatNumbers = (value: string | number = 0) => {
  const format = /[%]+/
  const converter = (val: any) => (+val).toLocaleString()

  return format.test(`${value}`)
    ? `${converter(`${value}`.split(format)[0])}%`
    : converter(value)
}

export const checkObjectProperty = (obj: any, prop: any) => {
  return prop in obj
}

export const terminateUserSession = async (
  logout: () => Promise<any>,
  site: {
    id?: number | string
    blocked?: boolean
    locale: string
    adminLocale: string
    createdAt: string
    region: string
    sharedUrl: {
      link?: string
      password?: string
    }
  },
) => {
  const { status } = await logout()
  //TODO: get fix of CORS error for local
  if (status >= 200 && status <= 299) {
    localStorage.removeItem('UNEXPECTED-THUNDER')
    const showAdminLoginPage = site.blocked
    if (showAdminLoginPage) {
      window.location.href = `${window.location.origin}/admin/login`
    } else {
      window.location.href = `${window.location.origin}/login`
    }
  }
}
